// Excel Import API client — Phase 4 Plan 04-02 Task 2.
//
// Wraps the 4 backend endpoints:
//   POST /api/v1/trips/import/upload                 (multipart)
//   POST /api/v1/trips/import/{import_id}/confirm-mapping
//   POST /api/v1/trips/import/{import_id}/revalidate-row
//   POST /api/v1/trips/import/{import_id}/commit    (Idempotency-Key required)
//
// Mirrors api/customerInvites.ts pattern (uses ApiClient).

import type { ApiClient } from "./client";

export interface ColumnMappingItem {
  user_header: string;
  mapped_to: string | null;
  confidence: number;
  needs_manual: boolean;
  suggested_alternatives: string[];
}

export interface UploadResponse {
  import_id: string;
  sheet_name: string;
  header_row: number;
  headers: string[];
  row_count: number;
  mapping: ColumnMappingItem[];
  preview_rows: unknown[][];
}

export interface PreviewRow {
  row_index: number;
  values: Record<string, unknown>;
  is_valid: boolean;
  errors: { field: string; reason: string }[];
  duplicate_of_existing: boolean;
}

export interface PreviewResponse {
  import_id: string;
  column_mapping: Record<string, string>;
  rows: PreviewRow[];
  valid_count: number;
  invalid_count: number;
  duplicate_count: number;
}

export interface CommitError {
  row_index: number;
  reason: string;
}

export interface CommitResult {
  import_id: string;
  created: number;
  skipped: number;
  errors: CommitError[];
}

function generateIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export const excelImportApi = {
  /**
   * Step 1 — POST .xlsx multipart upload.
   *
   * On 413/400 the server returns `{detail: string}`. The ApiClient throws
   * ApiError(status, detail) — caller catches + maps to UI banner.
   */
  async upload(client: ApiClient, file: File): Promise<UploadResponse> {
    const form = new FormData();
    form.append("file", file);
    // ApiClient.fetch sets Content-Type automatically for string bodies; for
    // FormData the browser must set it (with boundary) so we explicitly avoid
    // setting Content-Type and pass FormData as the body.
    return client.fetch<UploadResponse>("/api/v1/trips/import/upload", {
      method: "POST",
      body: form,
    });
  },

  /**
   * Step 2 → Step 3. User-confirmed mapping.
   */
  confirmMapping(
    client: ApiClient,
    importId: string,
    columnMapping: Record<string, string>,
  ): Promise<PreviewResponse> {
    return client.post<PreviewResponse>(`/api/v1/trips/import/${importId}/confirm-mapping`, {
      column_mapping: columnMapping,
    });
  },

  /**
   * Step 3 inline edit. Caller debounces 500ms.
   */
  revalidateRow(
    client: ApiClient,
    importId: string,
    rowIndex: number,
    updates: Record<string, unknown>,
  ): Promise<PreviewRow> {
    return client.post<PreviewRow>(`/api/v1/trips/import/${importId}/revalidate-row`, {
      row_index: rowIndex,
      updates,
    });
  },

  /**
   * Step 4. Idempotency-Key required (auto-generated client-side).
   */
  commit(client: ApiClient, importId: string, skipInvalid: boolean): Promise<CommitResult> {
    return client.fetch<CommitResult>(`/api/v1/trips/import/${importId}/commit`, {
      method: "POST",
      headers: {
        "Idempotency-Key": generateIdempotencyKey(),
      },
      body: JSON.stringify({ skip_invalid: skipInvalid }),
    });
  },
};
